/**
 * A stable id for THIS browser profile, used by the concurrent-login guard.
 *
 * The backend allows one desktop session and one mobile session per account and
 * asks before displacing an existing one. It stores the session id, but until
 * this existed it stored nothing about *whose* session that was — so a browser
 * that never reached the sign-out endpoint (a crash, a force-quit, a closed
 * tab) left its own session on record, and the next sign-in from that same
 * browser was indistinguishable from a stranger's second device. The user was
 * asked to "sign out the other device" to get back into their own machine.
 *
 * Sending this id on login lets the server recognise the slot's owner coming
 * back and skip the prompt.
 *
 * Not a security token. It authorises nothing: the only thing a match buys is
 * skipping a confirmation the user could have answered anyway. It is
 * deliberately opaque and carries nothing about the person or the machine.
 *
 * WHY A COOKIE AND NOT JUST localStorage
 * --------------------------------------
 * The id is worthless unless it OUTLIVES a logout — that is the entire case it
 * exists for. localStorage alone does not: `refreshAccessToken` in
 * checkTokenValidity.tsx calls a blanket `localStorage.clear()` when a refresh
 * fails (and SuperAdminLandingPage's forceLogout does the same), which happens
 * on essentially every sign-out, because the tokens are gone by then. That wiped
 * the id, so every login presented a NEW one, the server saw an unknown device,
 * and the prompt came back on every single sign-in — the exact bug this was
 * meant to fix.
 *
 * A cookie is immune to `localStorage.clear()`. Both stores are written and
 * either one can reseed the other, so the id survives a wipe of either.
 */

const STORAGE_KEY = 'device_uid';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 730; // ~2 years

/** Matches the server's accepted shape (<=64 chars of [A-Za-z0-9_-]). */
const VALID = /^[A-Za-z0-9_-]{1,64}$/;

const readCookie = (): string => {
  try {
    const match = document.cookie.match(
      new RegExp('(?:^|;\s*)' + STORAGE_KEY + '=([^;]*)'),
    );
    return match ? decodeURIComponent(match[1]) : '';
  } catch {
    return '';
  }
};

const writeCookie = (value: string): void => {
  try {
    // Lax so it is present on normal navigation; this cookie is never used for
    // auth, so it needs no Secure/HttpOnly treatment (and HttpOnly would make it
    // unreadable to the code that has to send it as a header).
    document.cookie =
      `${STORAGE_KEY}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax`;
  } catch {
    /* cookies unavailable - localStorage alone still works */
  }
};

const readLocal = (): string => {
  try {
    return localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

const writeLocal = (value: string): void => {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    /* storage blocked - the cookie alone still works */
  }
};

const generate = (): string => {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID().replace(/-/g, '');
    }
    if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
      const bytes = new Uint8Array(16);
      crypto.getRandomValues(bytes);
      return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    }
  } catch {
    /* fall through to the non-crypto path */
  }
  // Uniqueness is all that is required here — this value guards a UI prompt,
  // not access — so a weak fallback is acceptable where crypto is unavailable.
  return `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 12)}`;
};

/**
 * The id for this browser: created on first use, then kept for good.
 *
 * Whichever store still holds it wins, and the other is reseeded from it, so a
 * `localStorage.clear()` or a cookie purge costs nothing as long as one
 * survives. Returns '' only when neither store is writable, which tells the
 * server "this client cannot identify itself" — the pre-existing behaviour.
 */
export const getDeviceUid = (): string => {
  const fromCookie = readCookie();
  if (VALID.test(fromCookie)) {
    if (readLocal() !== fromCookie) writeLocal(fromCookie);
    return fromCookie;
  }

  const fromLocal = readLocal();
  if (VALID.test(fromLocal)) {
    writeCookie(fromLocal);
    return fromLocal;
  }

  const fresh = generate();
  writeCookie(fresh);
  writeLocal(fresh);

  // Confirm at least one store actually kept it. An id that is regenerated on
  // every call is worse than none: the server would record each new value as
  // the slot owner and never recognise the browser again.
  return readCookie() === fresh || readLocal() === fresh ? fresh : '';
};

/**
 * What KIND of client this is, sent as `X-Device-Type` on sign-in, sign-out and
 * 2FA verification, and stamped into the token's `device_type` claim.
 *
 * The server's concurrent-login guard reads the "Web " prefix before anything
 * else and files every web build - phone browser included - into the browser
 * slot, so the browser on a phone never competes with the native app for that
 * phone's slot. The desktop/mobile/tablet suffix is informational (login
 * history, the "signed in on ..." prompt), not what the slot is chosen by.
 *
 * Every caller must send the SAME string family: the class the slot was claimed
 * under at sign-in is the class every later refresh is checked against.
 */
export const getDeviceType = (): string => {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'Web Tablet';
  if (/Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(ua))
    return 'Web Mobile';
  return 'Web Desktop';
};
